import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../../Navigation/Sidebar/Sidebar";
import { Plus, Edit2, X, Search, ArrowLeft, GripVertical } from "lucide-react";
import varifiedIcon from "../../../assests/verifiedIcon.svg";
import { toastr } from "react-redux-toastr";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import "./SubCategoryAttributes.css";

// Sortable Heading Header Component
const SortableHeadingHeader = ({ heading, attributesCount, onEdit, onAddAttribute, ownerType, index, isEditable }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: heading.id || heading.heading, disabled: !isEditable });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const getOwnerBadge = () => {
    if (ownerType === "SUBCATEGORY") {
      return { text: "SUB CAT LEVEL", class: "subcat-level-badge-unique" };
    }
    return { text: "CAT LEVEL", class: "cat-level-badge-unique" };
  };

  const ownerBadge = getOwnerBadge();

  return (
    <div ref={setNodeRef} style={style} className="sortable-heading-header-unique">
      <div className="sortable-heading-title-unique">
        {isEditable && (
          <div className="drag-handle-wrapper-unique" {...attributes} {...listeners}>
            <GripVertical size={18} className="drag-handle-icon-unique" />
          </div>
        )}
        <div className="heading-border-wrapper-unique">
          <h2 className="heading-title-text-unique">{heading.heading}</h2>
        </div>
        <span className={`level-badge-unique ${ownerBadge.class}`}>{ownerBadge.text}</span>
        {isEditable && (
          <button 
            className="edit-heading-btn-unique" 
            onClick={() => onEdit(heading)} 
            title="Edit Heading"
          >
            <Edit2 size={14} />
          </button>
        )}
      </div>
      {isEditable && (
        <div className="heading-actions-wrapper-unique">
          <button className="add-attribute-btn-unique" onClick={() => onAddAttribute(heading)} title="Add Attribute">
            <Plus size={14} /> Add Attribute
          </button>
        </div>
      )}
    </div>
  );
};

const SubCategoryAttributesComponent = ({
  attributeHeadings = [],
  categoryName,
  subCategoryName,
  subCategoryId,
  loading,
  searchQuery,
  setSearchQuery,
  isSubmitting,
  onEditHeading,
  onBack,
  onAddAttribute,
  onEditAttribute,
  onDeleteAttribute,
  onDragEndHeadings,
  onAddHeadingClick,
  ownerType = "SUBCATEGORY"
}) => {
  const isEditable = ownerType === "SUBCATEGORY";

  const [showAddAttributeModal, setShowAddAttributeModal] = useState(false);
  const [selectedHeadingForAttribute, setSelectedHeadingForAttribute] = useState(null);
  const [newAttributeData, setNewAttributeData] = useState({
    key: "",
    dataType: "STRING",
    isRequired: "No",
    unitType: "No",
    dropdownOptions: [],
    isBoolean: "",
    unitValues: ""
  });
  const [dropdownInput, setDropdownInput] = useState("");
  const [editHeadingName, setEditHeadingName] = useState("");
  const [showEditHeadingModal, setShowEditHeadingModal] = useState(false);
  const [selectedHeadingForEdit, setSelectedHeadingForEdit] = useState(null);
  
  const [showEditAttributeModal, setShowEditAttributeModal] = useState(false);
  const [editAttributeData, setEditAttributeData] = useState({
    key: "",
    dataType: "STRING",
    isRequired: "No",
    unitType: "No",
    dropdownOptions: [],
    isBoolean: "",
    unitValues: ""
  });
  const [editDropdownInput, setEditDropdownInput] = useState("");
  const [editingAttribute, setEditingAttribute] = useState(null);
  const [editingHeadingForAttr, setEditingHeadingForAttr] = useState(null);
  
  const [showDeleteConfirmPopup, setShowDeleteConfirmPopup] = useState(false);
  const [attributeToDelete, setAttributeToDelete] = useState(null);
  const [headingForDelete, setHeadingForDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [popupTitle, setPopupTitle] = useState("");
  
  const [showOperationSuccessPopup, setShowOperationSuccessPopup] = useState(false);
  const [operationSuccessMessage, setOperationSuccessMessage] = useState("");
  const [operationSuccessTitle, setOperationSuccessTitle] = useState("");

  const dataTypeOptions = [
    { value: "STRING", label: "Text" },
    { value: "NUMBER", label: "Number" },
    { value: "DROPDOWN", label: "List" },
    { value: "BOOLEAN", label: "Boolean" },
  ];

  const isUnitAndRequiredEnabled = (dataType) => {
    return dataType === "DROPDOWN";
  };

  const getDataTypeBadgeClass = (dataType) => {
    switch (dataType) {
      case "STRING": return "data-type-badge-string-unique";
      case "NUMBER": return "data-type-badge-number-unique";
      case "DROPDOWN": return "data-type-badge-dropdown-unique";
      case "BOOLEAN": return "data-type-badge-boolean-unique";
      default: return "data-type-badge-string-unique";
    }
  };

  const getDisplayLabel = (dataType) => {
    if (dataType === "DROPDOWN") return "List";
    switch (dataType) {
      case "STRING": return "Text";
      case "NUMBER": return "Number";
      case "BOOLEAN": return "Boolean";
      default: return dataType;
    }
  };

  // ✅ Get Unit Display based on unitValues
  const getUnitDisplay = (attr) => {
    // For DROPDOWN type
    if (attr.dataType === "DROPDOWN") {
      // Check if unitValues exists and has at least one value
      if (attr.unitValues && Array.isArray(attr.unitValues) && attr.unitValues.length > 0) {
        return {
          label: "Yes",
          className: "unit-badge-yes-unique",
          values: attr.unitValues.join(', ')
        };
      }
      return {
        label: "No",
        className: "unit-badge-no-unique",
        values: null
      };
    }
    // For non-DROPDOWN types
    return {
      label: attr.unit || "No",
      className: attr.unit === "Yes" ? 'unit-badge-yes-unique' : 'unit-badge-no-unique',
      values: null
    };
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const addDropdownOption = () => {
    if (dropdownInput.trim() && !newAttributeData.dropdownOptions.includes(dropdownInput.trim())) {
      setNewAttributeData(prev => ({
        ...prev,
        dropdownOptions: [...prev.dropdownOptions, dropdownInput.trim()]
      }));
      setDropdownInput("");
    }
  };

  const removeDropdownOption = (option) => {
    setNewAttributeData(prev => ({
      ...prev,
      dropdownOptions: prev.dropdownOptions.filter(opt => opt !== option)
    }));
  };

  const addEditDropdownOption = () => {
    if (editDropdownInput.trim() && !editAttributeData.dropdownOptions.includes(editDropdownInput.trim())) {
      setEditAttributeData(prev => ({
        ...prev,
        dropdownOptions: [...prev.dropdownOptions, editDropdownInput.trim()]
      }));
      setEditDropdownInput("");
    }
  };

  const removeEditDropdownOption = (option) => {
    setEditAttributeData(prev => ({
      ...prev,
      dropdownOptions: prev.dropdownOptions.filter(opt => opt !== option)
    }));
  };

  const handleAddAttributeSubmit = () => {
    if (!newAttributeData.key.trim()) {
      toastr.warning("Warning", "Please enter attribute key");
      return;
    }
    
    if (newAttributeData.dataType === "BOOLEAN" && !newAttributeData.isBoolean) {
      toastr.warning("Warning", "Please select Yes/No for Boolean attribute");
      return;
    }
    
    if (newAttributeData.dataType === "DROPDOWN" && newAttributeData.unitType === "Yes" && !newAttributeData.unitValues.trim()) {
      toastr.warning("Warning", "Please enter unit values");
      return;
    }
    
    onAddAttribute(selectedHeadingForAttribute, newAttributeData, 
      (attributeName) => {
        setOperationSuccessTitle("Attribute Added");
        setOperationSuccessMessage(`"${attributeName}" has been added successfully!`);
        setShowOperationSuccessPopup(true);
      },
      (errorMsg) => {
        setPopupTitle("Add Failed");
        setPopupMessage(errorMsg || "Failed to add attribute. Please try again.");
        setShowErrorPopup(true);
      }
    );
    setShowAddAttributeModal(false);
    setSelectedHeadingForAttribute(null);
    setNewAttributeData({ key: "", dataType: "STRING", isRequired: "No", unitType: "No", dropdownOptions: [], isBoolean: "", unitValues: "" });
    setDropdownInput("");
  };

  const handleEditAttributeSubmit = () => {
    if (!editAttributeData.key.trim()) {
      toastr.warning("Warning", "Please enter attribute key");
      return;
    }
    
    if (editAttributeData.dataType === "BOOLEAN" && !editAttributeData.isBoolean) {
      toastr.warning("Warning", "Please select Yes/No for Boolean attribute");
      return;
    }
    
    if (editAttributeData.dataType === "DROPDOWN" && editAttributeData.unitType === "Yes" && !editAttributeData.unitValues.trim()) {
      toastr.warning("Warning", "Please enter unit values");
      return;
    }
    
    onEditAttribute(editingHeadingForAttr, editingAttribute, editAttributeData,
      (attributeName) => {
        setOperationSuccessTitle("Attribute Updated");
        setOperationSuccessMessage(`"${attributeName}" has been updated successfully!`);
        setShowOperationSuccessPopup(true);
      },
      (errorMsg) => {
        setPopupTitle("Update Failed");
        setPopupMessage(errorMsg || "Failed to update attribute. Please try again.");
        setShowErrorPopup(true);
      }
    );
    setShowEditAttributeModal(false);
    setEditingAttribute(null);
    setEditingHeadingForAttr(null);
    setEditAttributeData({ key: "", dataType: "STRING", isRequired: "No", unitType: "No", dropdownOptions: [], isBoolean: "", unitValues: "" });
    setEditDropdownInput("");
  };

  const openAddAttributeModal = (heading) => {
    if (!isEditable) {
      toastr.warning("Warning", "You cannot add attributes to CATEGORY level items");
      return;
    }
    setSelectedHeadingForAttribute(heading);
    setNewAttributeData({ key: "", dataType: "STRING", isRequired: "No", unitType: "No", dropdownOptions: [], isBoolean: "", unitValues: "" });
    setDropdownInput("");
    setShowAddAttributeModal(true);
  };

  const openEditAttributeModal = (heading, attribute) => {
    if (!isEditable) {
      toastr.warning("Warning", "You cannot edit attributes of CATEGORY level items");
      return;
    }
    setEditingHeadingForAttr(heading);
    setEditingAttribute(attribute);
    
    // ✅ Check unitValues to set unitType
    let unitTypeValue = "No";
    if (attribute.dataType === "DROPDOWN") {
      if (attribute.unitValues && Array.isArray(attribute.unitValues) && attribute.unitValues.length > 0) {
        unitTypeValue = "Yes";
      } else {
        unitTypeValue = "No";
      }
    } else {
      unitTypeValue = attribute.unit === "Yes" ? "Yes" : "No";
    }
    
    setEditAttributeData({
      key: attribute.key,
      dataType: attribute.dataType,
      isRequired: attribute.isRequired === true ? "Yes" : "No",
      unitType: unitTypeValue,
      dropdownOptions: attribute.dropdownOptions || [],
      isBoolean: attribute.isBoolean || "",
      unitValues: attribute.unitValues && Array.isArray(attribute.unitValues) 
        ? attribute.unitValues.join(', ') 
        : (attribute.unitValues || "")
    });
    setEditDropdownInput("");
    setShowEditAttributeModal(true);
  };

  const openEditHeadingModal = (heading) => {
    if (!isEditable) {
      toastr.warning("Warning", "You cannot edit headings of CATEGORY level items");
      return;
    }
    setSelectedHeadingForEdit(heading);
    setEditHeadingName(heading.heading);
    setShowEditHeadingModal(true);
  };

  const handleEditHeadingSubmit = () => {
    if (!editHeadingName.trim()) {
      toastr.warning("Warning", "Please enter heading name");
      return;
    }
    onEditHeading(selectedHeadingForEdit, editHeadingName.trim(),
      (headingName) => {
        setOperationSuccessTitle("Heading Updated");
        setOperationSuccessMessage(`Heading updated to "${headingName}" successfully!`);
        setShowOperationSuccessPopup(true);
      },
      (errorMsg) => {
        setPopupTitle("Update Failed");
        setPopupMessage(errorMsg || "Failed to update heading. Please try again.");
        setShowErrorPopup(true);
      }
    );
    setShowEditHeadingModal(false);
    setSelectedHeadingForEdit(null);
    setEditHeadingName("");
  };

  const handleDeleteClick = (heading, attribute) => {
    if (!isEditable) {
      toastr.warning("Warning", "You cannot delete attributes of CATEGORY level items");
      return;
    }
    setAttributeToDelete(attribute);
    setHeadingForDelete(heading);
    setShowDeleteConfirmPopup(true);
  };

  const handleConfirmDelete = () => {
    if (attributeToDelete && headingForDelete) {
      setIsDeleting(true);
      setShowDeleteConfirmPopup(false);
      
      onDeleteAttribute(
        headingForDelete, 
        attributeToDelete,
        (deletedKey) => {
          setIsDeleting(false);
          setOperationSuccessTitle("Attribute Deleted");
          setOperationSuccessMessage(`"${deletedKey}" has been removed successfully!`);
          setShowOperationSuccessPopup(true);
          setAttributeToDelete(null);
          setHeadingForDelete(null);
        },
        (errorMsg) => {
          setIsDeleting(false);
          setPopupTitle("Delete Failed");
          setPopupMessage(errorMsg || "Failed to delete attribute. Please try again.");
          setShowErrorPopup(true);
          setAttributeToDelete(null);
          setHeadingForDelete(null);
        }
      );
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirmPopup(false);
    setAttributeToDelete(null);
    setHeadingForDelete(null);
  };

  const handleCloseErrorPopup = () => {
    setShowErrorPopup(false);
    setPopupMessage("");
    setPopupTitle("");
  };

  const handleCloseOperationSuccessPopup = () => {
    setShowOperationSuccessPopup(false);
    setOperationSuccessMessage("");
    setOperationSuccessTitle("");
  };

  const handleDragEnd = (event) => {
    if (!isEditable) return;
    const { active, over } = event;
    if (active.id !== over.id) {
      const oldIndex = attributeHeadings.findIndex(h => (h.id || h.heading) === active.id);
      const newIndex = attributeHeadings.findIndex(h => (h.id || h.heading) === over.id);
      onDragEndHeadings(oldIndex, newIndex);
    }
  };

  if (loading) {
    return (
      <div className="subcat-attr-container-unique">
        <Sidebar />
        <div className="subcat-attr-loading-unique">
          <div className="spinner-unique"></div>
          <p className="loading-text-unique">Loading attributes...</p>
        </div>
      </div>
    );
  }

  const headingsWithIds = attributeHeadings.map((heading, idx) => ({
    ...heading,
    id: heading.id || heading.heading || `heading-${idx}`,
  }));

  const filteredHeadings = headingsWithIds.filter(heading => {
    if (!searchQuery) return true;
    const headingMatch = heading.heading.toLowerCase().includes(searchQuery.toLowerCase());
    const attributesMatch = (heading.productAttributeDtoList || []).some(attr =>
      attr.key.toLowerCase().includes(searchQuery.toLowerCase())
    );
    return headingMatch || attributesMatch;
  });

  return (
    <div className="subcat-attr-container-unique">
      <Sidebar />
      <div className="subcat-attr-content-unique">
        {/* Header */}
        <div className="subcat-attr-header-unique">
          <button className="subcat-attr-back-btn-unique" onClick={onBack}>
            <ArrowLeft size={20} />
          </button>
          <div className="subcat-attr-header-info-unique">
            <h1 className="header-title-unique">Attributes Configuration</h1>
            <div className="header-badges-wrapper-unique">
              <span className="badge-category-unique">Category: {categoryName}</span>
              <span className="badge-subcategory-unique">Sub Category: {subCategoryName}</span>
            </div>
          </div>
          {isEditable && (
            <button className="add-heading-header-btn-unique" onClick={onAddHeadingClick}>
              <Plus size={18} /> Add Attribute Category
            </button>
          )}
        </div>

        {/* Search */}
        <div className="search-wrapper-unique">
          <Search size={18} className="search-icon-unique" />
          <input
            type="text"
            placeholder="Search Attributes..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input-unique"
          />
          {searchQuery && (
            <button 
              className="search-clear-btn-unique"
              onClick={() => setSearchQuery("")}
              title="Clear search"
            >
              ✕
            </button>
          )}
        </div>

        {/* SCROLLABLE CONTAINER */}
        <div className="scrollable-container-unique">
          <div className="headings-list-unique">
            {filteredHeadings.length === 0 && (
              <div className="empty-state-unique">
                <div className="empty-icon-unique">{searchQuery ? "🔍" : "📋"}</div>
                <h3 className="empty-title-unique">{searchQuery ? "No Results Found" : "No Attribute Categories Found"}</h3>
                <p className="empty-description-unique">
                  {searchQuery 
                    ? `No attributes matching "${searchQuery}" were found.` 
                    : 'Click "Add Attribute Category" to create your first attribute category.'}
                </p>
                {searchQuery && (
                  <button className="clear-search-btn-unique" onClick={() => setSearchQuery("")}>
                    Clear Search
                  </button>
                )}
              </div>
            )}

            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={filteredHeadings.map(h => h.id)}
                strategy={verticalListSortingStrategy}
              >
                {filteredHeadings.map((heading, idx) => {
                  const attributes = heading.productAttributeDtoList || [];
                  const filteredAttributes = searchQuery
                    ? attributes.filter(attr => attr.key.toLowerCase().includes(searchQuery.toLowerCase()))
                    : attributes;
                  
                  const headingOwnerType = heading.ownerType || ownerType;
                  const isHeadingEditable = headingOwnerType === "SUBCATEGORY";

                  return (
                    <div key={heading.id} className="heading-card-unique">
                      <SortableHeadingHeader
                        heading={heading}
                        attributesCount={filteredAttributes.length}
                        onEdit={openEditHeadingModal}
                        onAddAttribute={openAddAttributeModal}
                        ownerType={headingOwnerType}
                        index={idx}
                        isEditable={isHeadingEditable}
                      />

                      <div className="attributes-list-unique">
                        {filteredAttributes.length === 0 ? (
                          <div className="no-attributes-state-unique">
                            <p className="no-attributes-text-unique">
                              No Attributes found for <strong className="heading-name-highlight-unique">"{heading.heading}"</strong>.
                            </p>
                            {isHeadingEditable && (
                              <button className="add-attribute-empty-btn-unique" onClick={() => openAddAttributeModal(heading)}>
                                <Plus size={14} /> Add Attribute
                              </button>
                            )}
                          </div>
                        ) : (
                          <div className="table-responsive-wrapper-unique">
                            <table className="attributes-table-unique">
                              <thead>
                                <tr className="table-header-row-unique">
                                  <th className="table-header-sno-unique">S.No.</th>
                                  <th className="table-header-name-unique">ATTRIBUTE NAME</th>
                                  <th className="table-header-type-unique">VALUE TYPE</th>
                                  <th className="table-header-unit-unique">UNIT TYPE</th>
                                  <th className="table-header-required-unique">REQUIRED</th>
                                  {isHeadingEditable && (
                                    <th className="table-header-action-unique">ACTION</th>
                                  )}
                                </tr>
                              </thead>
                              <tbody>
                                {filteredAttributes.map((attr, attrIdx) => {
                                  const unitInfo = getUnitDisplay(attr);
                                  return (
                                    <tr key={attr.id || attrIdx} className="table-row-unique">
                                      <td className="table-cell-sno-unique">{attr.priority || attrIdx + 1}</td>
                                      <td className="table-cell-name-unique">{attr.key}</td>
                                      <td className="table-cell-type-unique">
                                        <span className={`data-type-badge-unique ${getDataTypeBadgeClass(attr.dataType)}`}>
                                          {getDisplayLabel(attr.dataType)}
                                        </span>
                                      </td>
                                      <td className="table-cell-unit-unique">
                                        <span className={`unit-badge-unique ${unitInfo.className}`}>
                                          {unitInfo.label}
                                         
                                        </span>
                                      </td>
                                      <td className="table-cell-required-unique">
                                        <span className={`required-badge-unique ${attr.isRequired ? 'required-badge-yes-unique' : 'required-badge-no-unique'}`}>
                                          {attr.isRequired ? 'Yes' : 'No'}
                                        </span>
                                      </td>
                                      {isHeadingEditable && (
                                        <td className="table-cell-action-unique">
                                          <div className="action-buttons-wrapper-unique">
                                            <button 
                                              className="edit-attribute-btn-unique" 
                                              onClick={() => openEditAttributeModal(heading, attr)} 
                                              title="Edit Attribute"
                                            >
                                              <Edit2 size={14} />
                                            </button>
                                            <button 
                                              className="delete-attribute-btn-unique" 
                                              onClick={() => handleDeleteClick(heading, attr)} 
                                              title="Delete Attribute"
                                              disabled={isDeleting}
                                            >
                                              <X size={14} />
                                            </button>
                                          </div>
                                        </td>
                                      )}
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </SortableContext>
            </DndContext>
          </div>
        </div>
      </div>

      {/* ADD ATTRIBUTE MODAL */}
      {showAddAttributeModal && selectedHeadingForAttribute && isEditable && (
        <div className="modal-overlay-unique" onClick={() => setShowAddAttributeModal(false)}>
          <div className="add-attribute-modal-unique" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-unique">
              <h2 className="modal-title-unique">Add Attribute to "{selectedHeadingForAttribute.heading}"</h2>
              <button className="modal-close-btn-unique" onClick={() => setShowAddAttributeModal(false)}><X size={20} /></button>
            </div>
            <div className="modal-body-unique">
              <div className="form-group-unique">
                <label className="form-label-unique">Attribute Name <span className="required-star-unique">*</span></label>
                <input
                  type="text"
                  className="form-input-unique"
                  placeholder="e.g., Screen Type, Processor, RAM"
                  value={newAttributeData.key}
                  onChange={(e) => setNewAttributeData(prev => ({ ...prev, key: e.target.value }))}
                />
              </div>
              <div className="form-group-unique">
                <label className="form-label-unique">Value Type</label>
                <select
                  className="form-select-unique"
                  value={newAttributeData.dataType}
                  onChange={(e) => {
                    const newDataType = e.target.value;
                    setNewAttributeData(prev => ({ 
                      ...prev, 
                      dataType: newDataType, 
                      dropdownOptions: [],
                      isBoolean: "",
                      unitValues: "",
                      unitType: "No",
                      isRequired: "No"
                    }));
                  }}
                >
                  {dataTypeOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              {/* List Values - Only show for DROPDOWN type */}
              {newAttributeData.dataType === "DROPDOWN" && (
                <div className="form-group-unique">
                  <label className="form-label-unique">List Values <span className="required-star-unique">*</span></label>
                  <div className="dropdown-input-group-unique">
                    <input
                      type="text"
                      className="dropdown-input-unique"
                      placeholder="Enter option"
                      value={dropdownInput}
                      onChange={(e) => setDropdownInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && addDropdownOption()}
                    />
                    <button className="dropdown-add-btn-unique" onClick={addDropdownOption}>Add</button>
                  </div>
                  <div className="options-preview-unique">
                    {newAttributeData.dropdownOptions.map((opt, idx) => (
                      <span key={idx} className="option-tag-unique">
                        {opt}
                        <button className="option-remove-btn-unique" onClick={() => removeDropdownOption(opt)}><X size={12} /></button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {/* Boolean Values - Only show for BOOLEAN type */}
              {newAttributeData.dataType === "BOOLEAN" && (
                <div className="form-group-unique">
                  <label className="form-label-unique">Boolean Values <span className="required-star-unique">*</span></label>
                  <div className="radio-group-unique boolean-radio-group-unique">
                    <label className={`radio-label-unique ${newAttributeData.isBoolean === "Yes" ? 'radio-active-unique' : ''}`}>
                      <input 
                        type="radio" 
                        className="radio-input-unique"
                        value="Yes" 
                        checked={newAttributeData.isBoolean === "Yes"} 
                        onChange={() => setNewAttributeData(prev => ({ ...prev, isBoolean: "Yes" }))}
                      />
                      <span>Yes</span>
                    </label>
                    <label className={`radio-label-unique ${newAttributeData.isBoolean === "No" ? 'radio-active-unique' : ''}`}>
                      <input 
                        type="radio" 
                        className="radio-input-unique"
                        value="No" 
                        checked={newAttributeData.isBoolean === "No"} 
                        onChange={() => setNewAttributeData(prev => ({ ...prev, isBoolean: "No" }))}
                      />
                      <span>No</span>
                    </label>
                  </div>
                </div>
              )}
              {/* Unit Type - ONLY ENABLED for DROPDOWN type */}
              <div className="form-group-unique">
                <label className="form-label-unique">Unit Type</label>
                <div className="radio-group-unique">
                  <label className={`radio-label-unique ${newAttributeData.unitType === "Yes" ? 'radio-active-unique' : ''} ${!isUnitAndRequiredEnabled(newAttributeData.dataType) ? 'radio-disabled-unique' : ''}`}>
                    <input 
                      type="radio" 
                      className="radio-input-unique"
                      value="Yes" 
                      checked={newAttributeData.unitType === "Yes"} 
                      onChange={() => {
                        setNewAttributeData(prev => ({ 
                          ...prev, 
                          unitType: "Yes"
                        }));
                      }}
                      disabled={!isUnitAndRequiredEnabled(newAttributeData.dataType) || isSubmitting}
                    />
                    <span>Yes</span>
                  </label>
                  <label className={`radio-label-unique ${newAttributeData.unitType === "No" ? 'radio-active-unique' : ''} ${!isUnitAndRequiredEnabled(newAttributeData.dataType) ? 'radio-disabled-unique' : ''}`}>
                    <input 
                      type="radio" 
                      className="radio-input-unique"
                      value="No" 
                      checked={newAttributeData.unitType === "No"} 
                      onChange={() => {
                        setNewAttributeData(prev => ({ 
                          ...prev, 
                          unitType: "No",
                          unitValues: ""
                        }));
                      }}
                      disabled={!isUnitAndRequiredEnabled(newAttributeData.dataType) || isSubmitting}
                    />
                    <span>No</span>
                  </label>
                </div>
              </div>
              {/* Unit Values - Only show when Unit Type is Yes AND dataType is DROPDOWN */}
              {newAttributeData.unitType === "Yes" && isUnitAndRequiredEnabled(newAttributeData.dataType) && (
                <div className="form-group-unique">
                  <label className="form-label-unique">Unit Values <span className="required-star-unique">*</span></label>
                  <input
                    type="text"
                    className="form-input-unique"
                    placeholder="GB, TB, MB, KG, GM"
                    value={newAttributeData.unitValues || ""}
                    onChange={(e) => setNewAttributeData(prev => ({ ...prev, unitValues: e.target.value }))}
                    disabled={isSubmitting}
                  />
                 
                
                </div>
              )}
              {/* Is Required - ONLY ENABLED for DROPDOWN type */}
              <div className="form-group-unique">
                <label className="form-label-unique">Is Required?</label>
                <div className="radio-group-unique">
                  <label className={`radio-label-unique ${newAttributeData.isRequired === "Yes" ? 'radio-active-unique' : ''} ${!isUnitAndRequiredEnabled(newAttributeData.dataType) ? 'radio-enabled-unique' : ''}`}>
                    <input 
                      type="radio" 
                      className="radio-input-unique"
                      value="Yes" 
                      checked={newAttributeData.isRequired === "Yes"} 
                      onChange={() => setNewAttributeData(prev => ({ ...prev, isRequired: "Yes" }))}
                      // disabled={!isUnitAndRequiredEnabled(newAttributeData.dataType) || isSubmitting}
                    />
                    <span>Yes</span>
                  </label>
                  <label className={`radio-label-unique ${newAttributeData.isRequired === "No" ? 'radio-active-unique' : ''} ${!isUnitAndRequiredEnabled(newAttributeData.dataType) ? 'radio-enabled-unique' : ''}`}>
                    <input 
                      type="radio" 
                      className="radio-input-unique"
                      value="No" 
                      checked={newAttributeData.isRequired === "No"} 
                      onChange={() => setNewAttributeData(prev => ({ ...prev, isRequired: "No" }))}
                      // disabled={!isUnitAndRequiredEnabled(newAttributeData.dataType) || isSubmitting}
                    />
                    <span>No</span>
                  </label>
                </div>
              </div>
            </div>
            <div className="modal-footer-unique">
              <button className="modal-cancel-btn-unique" onClick={() => setShowAddAttributeModal(false)}>Cancel</button>
              <button className="modal-submit-btn-unique" onClick={handleAddAttributeSubmit} disabled={
                !newAttributeData.key.trim() || 
                (newAttributeData.dataType === "DROPDOWN" && newAttributeData.dropdownOptions.length === 0) ||
                (newAttributeData.dataType === "BOOLEAN" && !newAttributeData.isBoolean) ||
                (newAttributeData.dataType === "DROPDOWN" && newAttributeData.unitType === "Yes" && !newAttributeData.unitValues.trim()) ||
                isSubmitting
              }>
                Add Attribute
              </button>
            </div>
          </div>
        </div>
      )}

      {/* EDIT ATTRIBUTE MODAL */}
      {showEditAttributeModal && editingAttribute && editingHeadingForAttr && isEditable && (
        <div className="modal-overlay-unique" onClick={() => setShowEditAttributeModal(false)}>
          <div className="edit-attribute-modal-unique" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-unique">
              <h2 className="modal-title-unique">Edit Attribute</h2>
              <button className="modal-close-btn-unique" onClick={() => setShowEditAttributeModal(false)}><X size={20} /></button>
            </div>
            <div className="modal-body-unique">
              <div className="form-group-unique">
                <label className="form-label-unique">Attribute Name <span className="required-star-unique">*</span></label>
                <input
                  type="text"
                  className="form-input-unique"
                  placeholder="e.g., Screen Type, Processor, RAM"
                  value={editAttributeData.key}
                  onChange={(e) => setEditAttributeData(prev => ({ ...prev, key: e.target.value }))}
                />
              </div>
              <div className="form-group-unique">
                <label className="form-label-unique">Value Type</label>
                <select
                  className="form-select-unique"
                  value={editAttributeData.dataType}
                  onChange={(e) => {
                    const newDataType = e.target.value;
                    setEditAttributeData(prev => ({ 
                      ...prev, 
                      dataType: newDataType, 
                      dropdownOptions: [],
                      isBoolean: "",
                      unitValues: "",
                      unitType: "No",
                      isRequired: "No"
                    }));
                  }}
                >
                  {dataTypeOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              {/* List Values - Only show for DROPDOWN type */}
              {editAttributeData.dataType === "DROPDOWN" && (
                <div className="form-group-unique">
                  <label className="form-label-unique">List Values <span className="required-star-unique">*</span></label>
                  <div className="dropdown-input-group-unique">
                    <input
                      type="text"
                      className="dropdown-input-unique"
                      placeholder="Enter option"
                      value={editDropdownInput}
                      onChange={(e) => setEditDropdownInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && addEditDropdownOption()}
                    />
                    <button className="dropdown-add-btn-unique" onClick={addEditDropdownOption}>Add</button>
                  </div>
                  <div className="options-preview-unique">
                    {editAttributeData.dropdownOptions.map((opt, idx) => (
                      <span key={idx} className="option-tag-unique">
                        {opt}
                        <button className="option-remove-btn-unique" onClick={() => removeEditDropdownOption(opt)}><X size={12} /></button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {/* Boolean Values - Only show for BOOLEAN type */}
              {editAttributeData.dataType === "BOOLEAN" && (
                <div className="form-group-unique">
                  <label className="form-label-unique">Boolean Values <span className="required-star-unique">*</span></label>
                  <div className="radio-group-unique boolean-radio-group-unique">
                    <label className={`radio-label-unique ${editAttributeData.isBoolean === "Yes" ? 'radio-active-unique' : ''}`}>
                      <input 
                        type="radio" 
                        className="radio-input-unique"
                        value="Yes" 
                        checked={editAttributeData.isBoolean === "Yes"} 
                        onChange={() => setEditAttributeData(prev => ({ ...prev, isBoolean: "Yes" }))}
                      />
                      <span>Yes</span>
                    </label>
                    <label className={`radio-label-unique ${editAttributeData.isBoolean === "No" ? 'radio-active-unique' : ''}`}>
                      <input 
                        type="radio" 
                        className="radio-input-unique"
                        value="No" 
                        checked={editAttributeData.isBoolean === "No"} 
                        onChange={() => setEditAttributeData(prev => ({ ...prev, isBoolean: "No" }))}
                      />
                      <span>No</span>
                    </label>
                  </div>
                </div>
              )}
              {/* Unit Type - ONLY ENABLED for DROPDOWN type */}
              <div className="form-group-unique">
                <label className="form-label-unique">Unit Type</label>
                <div className="radio-group-unique">
                  <label className={`radio-label-unique ${editAttributeData.unitType === "Yes" ? 'radio-active-unique' : ''} ${!isUnitAndRequiredEnabled(editAttributeData.dataType) ? 'radio-disabled-unique' : ''}`}>
                    <input 
                      type="radio" 
                      className="radio-input-unique"
                      value="Yes" 
                      checked={editAttributeData.unitType === "Yes"} 
                      onChange={() => {
                        setEditAttributeData(prev => ({ 
                          ...prev, 
                          unitType: "Yes"
                        }));
                      }}
                      disabled={!isUnitAndRequiredEnabled(editAttributeData.dataType) || isSubmitting}
                    />
                    <span>Yes</span>
                  </label>
                  <label className={`radio-label-unique ${editAttributeData.unitType === "No" ? 'radio-active-unique' : ''} ${!isUnitAndRequiredEnabled(editAttributeData.dataType) ? 'radio-disabled-unique' : ''}`}>
                    <input 
                      type="radio" 
                      className="radio-input-unique"
                      value="No" 
                      checked={editAttributeData.unitType === "No"} 
                      onChange={() => {
                        setEditAttributeData(prev => ({ 
                          ...prev, 
                          unitType: "No",
                          unitValues: ""
                        }));
                      }}
                      disabled={!isUnitAndRequiredEnabled(editAttributeData.dataType) || isSubmitting}
                    />
                    <span>No</span>
                  </label>
                </div>
              </div>
              {/* Unit Values - Only show when Unit Type is Yes AND dataType is DROPDOWN */}
              {editAttributeData.unitType === "Yes" && isUnitAndRequiredEnabled(editAttributeData.dataType) && (
                <div className="form-group-unique">
                  <label className="form-label-unique">Unit Values <span className="required-star-unique">*</span></label>
                  <input
                    type="text"
                    className="form-input-unique"
                    placeholder="GB, TB, MB, KG, GM"
                    value={editAttributeData.unitValues || ""}
                    onChange={(e) => setEditAttributeData(prev => ({ ...prev, unitValues: e.target.value }))}
                    disabled={isSubmitting}
                  />
                 
                </div>
              )}
              {/* Is Required - ONLY ENABLED for DROPDOWN type */}
              <div className="form-group-unique">
                <label className="form-label-unique">Is Required?</label>
                <div className="radio-group-unique">
                  <label className={`radio-label-unique ${editAttributeData.isRequired === "Yes" ? 'radio-active-unique' : ''} ${!isUnitAndRequiredEnabled(editAttributeData.dataType) ? 'radio-enabled-unique' : ''}`}>
                    <input 
                      type="radio" 
                      className="radio-input-unique"
                      value="Yes" 
                      checked={editAttributeData.isRequired === "Yes"} 
                      onChange={() => setEditAttributeData(prev => ({ ...prev, isRequired: "Yes" }))}
                      // disabled={!isUnitAndRequiredEnabled(editAttributeData.dataType) || isSubmitting}
                    />
                    <span>Yes</span>
                  </label>
                  <label className={`radio-label-unique ${editAttributeData.isRequired === "No" ? 'radio-active-unique' : ''} ${!isUnitAndRequiredEnabled(editAttributeData.dataType) ? 'radio-enabled-unique' : ''}`}>
                    <input 
                      type="radio" 
                      className="radio-input-unique"
                      value="No" 
                      checked={editAttributeData.isRequired === "No"} 
                      onChange={() => setEditAttributeData(prev => ({ ...prev, isRequired: "No" }))}
                      // disabled={!isUnitAndRequiredEnabled(editAttributeData.dataType) || isSubmitting}
                    />
                    <span>No</span>
                  </label>
                </div>
              </div>
            </div>
            <div className="modal-footer-unique">
              <button className="modal-cancel-btn-unique" onClick={() => setShowEditAttributeModal(false)}>Cancel</button>
              <button className="modal-submit-btn-unique" onClick={handleEditAttributeSubmit} disabled={
                !editAttributeData.key.trim() || 
                (editAttributeData.dataType === "DROPDOWN" && editAttributeData.dropdownOptions.length === 0) ||
                (editAttributeData.dataType === "BOOLEAN" && !editAttributeData.isBoolean) ||
                (editAttributeData.dataType === "DROPDOWN" && editAttributeData.unitType === "Yes" && !editAttributeData.unitValues.trim()) ||
                isSubmitting
              }>
                Update Attribute
              </button>
            </div>
          </div>
        </div>
      )}

      {/* EDIT HEADING MODAL */}
      {showEditHeadingModal && selectedHeadingForEdit && isEditable && (
        <div className="modal-overlay-unique" onClick={() => setShowEditHeadingModal(false)}>
          <div className="edit-heading-modal-unique" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-unique">
              <h2 className="modal-title-unique">Edit Heading</h2>
              <button className="modal-close-btn-unique" onClick={() => setShowEditHeadingModal(false)}><X size={20} /></button>
            </div>
            <div className="modal-body-unique">
              <div className="form-group-unique">
                <label className="form-label-unique">Heading Name <span className="required-star-unique">*</span></label>
                <input
                  type="text"
                  className="form-input-unique"
                  placeholder="Enter heading name"
                  value={editHeadingName}
                  onChange={(e) => setEditHeadingName(e.target.value)}
                />
              </div>
            </div>
            <div className="modal-footer-unique">
              <button className="modal-cancel-btn-unique" onClick={() => setShowEditHeadingModal(false)}>Cancel</button>
              <button className="modal-submit-btn-unique" onClick={handleEditHeadingSubmit} disabled={!editHeadingName.trim() || isSubmitting}>
                Update Heading
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DELETE CONFIRMATION POPUP */}
      {showDeleteConfirmPopup && attributeToDelete && isEditable && (
        <div className="modal-overlay-unique" onClick={handleCancelDelete}>
          <div className="delete-confirm-modal-unique" onClick={(e) => e.stopPropagation()}>
            <div className="delete-modal-content-unique">
              <div className="delete-icon-wrapper-unique">
                <X size={48} strokeWidth={1.5} />
              </div>
              <h3 className="delete-title-unique">Remove Attribute</h3>
              <p className="delete-message-unique">
                Are you sure you want to remove "{attributeToDelete.key}" from this Attributes Group?
              </p>
              <div className="delete-actions-unique">
                <button className="delete-cancel-btn-unique" onClick={handleCancelDelete} disabled={isDeleting}>
                  Cancel
                </button>
                <button className="delete-confirm-btn-unique" onClick={handleConfirmDelete} disabled={isDeleting}>
                  {isDeleting ? "Deleting..." : "Yes, Remove"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* OPERATION SUCCESS POPUP */}
      {showOperationSuccessPopup && (
        <div className="modal-overlay-unique" onClick={handleCloseOperationSuccessPopup}>
          <div className="success-modal-unique" onClick={(e) => e.stopPropagation()}>
            <div className="success-icon-wrapper-unique">
              <img src={varifiedIcon} alt="success" />
            </div>
            <h3 className="success-title-unique">{operationSuccessTitle}</h3>
            <p className="success-message-unique">{operationSuccessMessage}</p>
            <button className="success-done-btn-unique" onClick={handleCloseOperationSuccessPopup}>
              Done
            </button>
          </div>
        </div>
      )}

      {/* ERROR POPUP */}
      {showErrorPopup && (
        <div className="modal-overlay-unique" onClick={handleCloseErrorPopup}>
          <div className="error-modal-unique" onClick={(e) => e.stopPropagation()}>
            <div className="error-icon-wrapper-unique">
              <X size={48} strokeWidth={1.5} />
            </div>
            <h3 className="error-title-unique">{popupTitle}</h3>
            <p className="error-message-unique">{popupMessage}</p>
            <button className="error-tryagain-btn-unique" onClick={handleCloseErrorPopup}>
              Try Again
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubCategoryAttributesComponent;